import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Search, X, Calendar } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import FilterDropdown from '../components/FilterDropdown';
import './Reports.css';

const PAGE_SIZE = 10;

const fmtShort = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : null;

function getPageNumbers(current, total) {
  const delta = 1;
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      pages.push(i);
    }
  }
  const withDots = [];
  let prev;
  for (const p of pages) {
    if (prev !== undefined && p - prev > 1) withDots.push('…');
    withDots.push(p);
    prev = p;
  }
  return withDots;
}

const REPORTS_STATE_KEY = 'admin_reports_filters';

const loadSavedReportsState = () => {
  try {
    const saved = sessionStorage.getItem(REPORTS_STATE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const Reports = () => {
  const navigate = useNavigate();
  const savedState = loadSavedReportsState();

  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(savedState.page || 1);

  const [search, setSearch] = useState(savedState.search || '');
  const [debounced, setDebounced] = useState(savedState.search || '');
  const [status, setStatus] = useState(savedState.status || '');
  const [reportType, setReportType] = useState(savedState.reportType || '');
  const [modalityId, setModalityId] = useState(savedState.modalityId || '');
  const [bodyPartId, setBodyPartId] = useState(savedState.bodyPartId || '');
  const [dateFrom, setDateFrom] = useState(savedState.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedState.dateTo || '');

  const [modalities, setModalities] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const dateRangeRef = useRef(null);

  const [allUsers, setAllUsers] = useState([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(savedState.selectedUserId || '');
  const searchWrapRef = useRef(null);
  useEffect(() => {
    adminAPI.getModalities().then(r => setModalities(r.data || [])).catch(() => { });
    adminAPI.getBodyParts().then(r => setBodyParts(r.data || [])).catch(() => { });
    adminAPI.getUsers().then(r => setAllUsers(r.data || [])).catch(() => { });
  }, []);


  useEffect(() => {
    const onClick = (e) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) setDateRangeOpen(false);
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setUserDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback((p) => {
    setLoading(true);
    const params = { page: p, limit: PAGE_SIZE };
    if (selectedUserId) params.user_id = selectedUserId;
    else if (debounced) params.search = debounced;
    if (status) params.status = status;
    if (reportType) params.report_type = reportType;
    if (modalityId) params.modality_id = modalityId;
    if (bodyPartId) params.body_part_id = bodyPartId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    adminAPI.getReports(params)
      .then(r => {
        setReports(r.data?.data || []);
        setPagination(r.data?.pagination || {});
        setPage(p);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [debounced, selectedUserId, status, reportType, modalityId, bodyPartId, dateFrom, dateTo]);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      fetchPage(savedState.page || 1);
    } else {
      fetchPage(1);
    }
  }, [fetchPage]);

  useEffect(() => {
    try {
      sessionStorage.setItem(REPORTS_STATE_KEY, JSON.stringify({
        page, search, selectedUserId, status, reportType, modalityId, bodyPartId, dateFrom, dateTo,
      }));
    } catch { /* noop */ }
  }, [page, search, selectedUserId, status, reportType, modalityId, bodyPartId, dateFrom, dateTo]);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const totalPages = pagination.totalPages || 1;
  const total = pagination.total || 0;

  const clearFilters = () => { setStatus(''); setReportType(''); setModalityId(''); setBodyPartId(''); setDateFrom(''); setDateTo(''); };
  const clearAllIncludingSearch = () => {
    clearFilters(); setSearch(''); setSelectedUserId('');
    try { sessionStorage.removeItem(REPORTS_STATE_KEY); } catch { /* noop */ }
  };

  const modalityName = modalities.find(m => m.id === modalityId)?.name;
  const bodyPartName = bodyParts.find(b => b.id === bodyPartId)?.name;

  const activeChips = [];
  if (status) activeChips.push({ key: 'status', label: `Status: ${status === 'completed' ? 'Completed' : 'Pending'}`, clear: () => setStatus('') });
  if (reportType) activeChips.push({ key: 'type', label: `Type: ${reportType === 'hospital' ? 'Hospital' : 'Non Hospital'}`, clear: () => setReportType('') });
  if (modalityId) activeChips.push({ key: 'modality', label: `Modality: ${modalityName || '—'}`, clear: () => setModalityId('') });
  if (bodyPartId) activeChips.push({ key: 'bodypart', label: `Body Part: ${bodyPartName || '—'}`, clear: () => setBodyPartId('') });
  if (dateFrom || dateTo) activeChips.push({ key: 'date', label: `Date: ${fmtShort(dateFrom) || '…'} → ${fmtShort(dateTo) || '…'}`, clear: () => { setDateFrom(''); setDateTo(''); } });

  const dateRangeLabel = (dateFrom || dateTo) ? `${fmtShort(dateFrom) || '…'} - ${fmtShort(dateTo) || '…'}` : 'Select';

  const filteredUsers = (() => {
    const q = search.trim().toLowerCase();
    const usersOnly = allUsers.filter(u => u.role === 'user');
    const list = q
      ? usersOnly.filter(u => u.first_name?.toLowerCase().includes(q))
      : usersOnly;
    return list.slice(0, 50);
  })();

  return (
    <div className="admin-reports-page">
      <h1 className="admin-page-title">Reports</h1>

      <div className="admin-reports-toolbar">
        <div className="admin-search-wrap" ref={searchWrapRef}>
          <Search size={15} className="admin-search-icon" />
          <input
            className="admin-search-input"
            placeholder="Search by user name..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedUserId(''); }}
            onFocus={() => setUserDropdownOpen(true)}
          />
          {search && (
            <button
              type="button"
              className="admin-search-clear-btn"
              onClick={() => { setSearch(''); setSelectedUserId(''); setUserDropdownOpen(false); }}
              aria-label="Clear search"
            >
              <X size={13} />
            </button>
          )}
          {userDropdownOpen && (
            <div className="admin-user-dropdown">
              {filteredUsers.length === 0 ? (
                <div className="admin-user-dropdown-empty">No users found</div>
              ) : (
                filteredUsers.map(u => {
                  const name = u.first_name || u.email;
                  return (
                    <div
                      key={u.id}
                      className="admin-user-dropdown-item"
                      onMouseDown={() => { setSearch(name); setSelectedUserId(u.id); setUserDropdownOpen(false); }}
                    >
                      <span className="admin-user-dropdown-name">{name}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <button
          className={`admin-filter-toggle-btn ${filterOpen ? 'active' : ''}`}
          onClick={() => setFilterOpen(o => !o)}
        >
          <Filter size={14} /> Filters
          {activeChips.length > 0 && <span className="admin-filter-badge">{activeChips.length}</span>}
        </button>
      </div>

      {filterOpen && (
        <div className="admin-filter-row-card">
          <div className="admin-filter-field">
            <label>Status</label>
            <FilterDropdown
              value={status}
              onChange={setStatus}
              options={[
                { value: '', label: 'All' },
                { value: 'pending', label: 'Pending' },
                { value: 'completed', label: 'Completed' },
              ]}
              align="left"
            />
          </div>

          <div className="admin-filter-field">
            <label>Type</label>
            <FilterDropdown
              value={reportType}
              onChange={setReportType}
              options={[
                { value: '', label: 'All' },
                { value: 'hospital', label: 'Hospital' },
                { value: 'non_hospital', label: 'Non Hospital' },
              ]}
              align="left"
            />
          </div>

          <div className="admin-filter-field">
            <label>Modality</label>
            <FilterDropdown
              value={modalityId}
              onChange={setModalityId}
              options={[{ value: '', label: 'All' }, ...modalities.map(m => ({ value: m.id, label: m.name }))]}
              align="left"
            />
          </div>

          <div className="admin-filter-field">
            <label>Body Part</label>
            <FilterDropdown
              value={bodyPartId}
              onChange={setBodyPartId}
              options={[{ value: '', label: 'All' }, ...bodyParts.map(b => ({ value: b.id, label: b.name }))]}
              align="left"
            />
          </div>

          <div className="admin-filter-field admin-daterange-wrap" ref={dateRangeRef}>
            <label>Date Range</label>
            <button
              type="button"
              className="admin-daterange-trigger"
              onClick={() => setDateRangeOpen(o => !o)}
            >
              <span className={(dateFrom || dateTo) ? '' : 'admin-daterange-placeholder'}>{dateRangeLabel}</span>
              <Calendar size={14} />
            </button>

            {dateRangeOpen && (
              <div className="admin-daterange-popover">
                <div className="admin-daterange-field">
                  <label>Start Date</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo || undefined} />
                </div>
                <div className="admin-daterange-field">
                  <label>End Date</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} min={dateFrom || undefined} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeChips.length > 0 && (
        <div className="admin-active-filters">
          <span className="admin-active-label">Active:</span>
          {activeChips.map(c => (
            <span key={c.key} className="admin-active-chip">
              {c.label}
              <button type="button" onClick={c.clear} aria-label={`Remove ${c.label} filter`}>
                <X size={11} />
              </button>
            </span>
          ))}
          <button type="button" className="admin-active-clearall" onClick={clearAllIncludingSearch}>
            Clear all
          </button>
        </div>
      )}

      <div className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th><th>Modality</th><th>Body Part</th><th>Technique</th><th>Type</th>
              <th>Status</th><th>Updated At</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="admin-table-empty">Loading…</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={9} className="admin-table-empty">No reports match these filters.</td></tr>
            ) : (
              reports.map(r => (
                <tr key={r.id}>
                  <td>{[r.profiles?.first_name, r.profiles?.last_name].filter(Boolean).join(' ') || r.profiles?.email || '—'}</td>
                  <td>{r.modalities?.name || '—'}</td>
                  <td>{r.body_parts?.name || '—'}</td>
                  <td>{r.techniques?.name || '—'}</td>
                  <td>
                    {r.report_type
                      ? <span className={`type-pill ${r.report_type}`}>{r.report_type === 'hospital' ? 'Hospital' : 'Non Hospital'}</span>
                      : '—'}
                  </td>
                  <td><span className={`admin-status-badge ${r.status === 'completed' ? 'active' : 'inactive'}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1).toLowerCase()}</span></td>
                  {/* <td>{r.feedback?.length > 0 ? <span className="admin-fb-count">{r.feedback.length}</span> : '—'}</td> */}
                  <td>
                    <div>{fmtDate(r.updated_at || r.created_at)}</div>
                    <div className="admin-created-time">{fmtTime(r.updated_at || r.created_at)}</div>
                  </td>
                  <td>
                    <button className="admin-view-btn" onClick={() => navigate(`/reports/${r.id}`)}>View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="pagination">
          <span className="pg-info">
            Showing {total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0} to {Math.min(page * PAGE_SIZE, total)} of {total} entries
          </span>
          <div className="pg-controls">
            <button className="pg-btn pg-btn-word" onClick={() => fetchPage(page - 1)} disabled={page === 1}>Previous</button>
            {getPageNumbers(page, totalPages).map((p, idx) => (
              p === '…'
                ? <span key={`dots-${idx}`} className="pg-dots">…</span>
                : <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => fetchPage(p)}>{p}</button>
            ))}
            <button className="pg-btn pg-btn-word" onClick={() => fetchPage(page + 1)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Reports;